import type { ReactElement } from 'react';
import React, { memo } from 'react';
import type { ReactPaginateProps } from 'react-paginate';
import ReactPaginate from 'react-paginate';
import { t } from '@lingui/core/macro';
import styled from 'styled-components';

import CarouselLeft from '@rover/icons/svg/carousel-left.svg';
import CarouselRight from '@rover/icons/svg/carousel-right.svg';

export type Props = ReactPaginateProps;

export const PaginationWrapper = styled.nav`
  user-select: none;

  ul {
    display: flex;
    padding: 0;
  }

  li {
    align-items: center;
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    cursor: pointer;
    display: flex;
    font-size: 16px;
    justify-content: center;
    min-height: 40px;
    min-width: 44px;

    &:not(:last-child) {
      margin-right: -1px;
    }

    &:first-child,
    &:first-child > a {
      border-top-left-radius: 20px;
      border-bottom-left-radius: 20px;
    }

    &:last-child,
    &:last-child > a {
      border-top-right-radius: 20px;
      border-bottom-right-radius: 20px;
    }

    & > a {
      position: relative;
      top: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      color: ${({ theme }) => theme.textColor.tertiary};
      width: 44px;
      height: 40px;
      text-decoration: none;
    }

    &.selected > a {
      padding-top: 2px;
      border-bottom: ${({ theme }) =>
        `${theme.borderWidth.primary} solid ${theme.colors.interactive_border.primary_active}`};
      color: ${({ theme }) => theme.textColor.secondary};
      font-weight: bold;
    }

    &.disabled {
      cursor: not-allowed;

      & a > svg {
        fill: ${({ theme }) => theme.colors.interactive_text.disabled};
      }
    }
  }
`;

const LeftArrow = styled(CarouselLeft)`
  width: 16px;
  height: 16px;
`;
const RightArrow = styled(CarouselRight)`
  width: 16px;
  height: 16px;
`;

const blur = ({ target }) => {
  setTimeout(() => {
    target.blur();
    target.parentNode.blur();
  });
};

const Pagination = ({ marginPagesDisplayed = 0, ...props }: Props): ReactElement => {
  return (
    <PaginationWrapper onMouseDown={blur} onMouseUp={blur} aria-label={t`Pagination`}>
      <ReactPaginate
        previousLabel={<LeftArrow />}
        previousAriaLabel={t`Previous page`}
        breakLabel={<div aria-label={t`Other pages`}>...</div>}
        nextLabel={<RightArrow />}
        nextAriaLabel={t`Next page`}
        marginPagesDisplayed={marginPagesDisplayed}
        {...props}
      />
    </PaginationWrapper>
  );
};

export default memo(Pagination);
